import { TableCell, TableRow } from '@/src/ui-components';
import { useEffect, useState } from 'react';
import { BookOffer } from 'xrpl';
import { useOfferValues } from '../hooks';
import { OfferType } from '../orderbook.types';

type OfferProps = {
  offer: BookOffer;
  offerType: OfferType;
  isNew?: boolean;
  maxTotal?: number;
};

/**
 * Component to display an offer
 * @param offer BookOffer object
 * @param offerType OfferType (buy or sell)
 * @param isNew Whether this is a new offer (triggers flash animation)
 * @param maxTotal Maximum total across all offers (for depth visualization)
 * @returns React.ReactNode
 */
export const Offer = ({ offer, offerType, isNew = false, maxTotal }: OfferProps) => {
  const { amount, total, price } = useOfferValues(offer, offerType);
  const [shouldFlash, setShouldFlash] = useState(false);

  // Trigger flash animation when isNew prop changes to true
  useEffect(() => {
    if (isNew) {
      setShouldFlash(true);
      const timer = setTimeout(() => setShouldFlash(false), 600);
      return () => clearTimeout(timer);
    }
  }, [isNew]);

  const textColor = offerType === 'buy' ? 'text-bullish' : 'text-bearish';
  const flashAnimation = shouldFlash ? (offerType === 'buy' ? 'animate-flash-bullish' : 'animate-flash-bearish') : '';

  // Calculate depth visualization percentage
  const depthPercentage = maxTotal ? (Number(total) / maxTotal) * 100 : 0;
  const depthColor = offerType === 'buy'
    ? 'hsl(var(--bullish) / 0.25)'
    : 'hsl(var(--bearish) / 0.25)';

  return (
    <TableRow
      className={`${flashAnimation} text-xs font-mono hover:bg-muted/50 transition-colors cursor-pointer`}
    >
      <TableCell className={`p-1 ${textColor} font-semibold`}>{price}</TableCell>
      <TableCell className="p-1 text-right">{amount}</TableCell>
      <TableCell className="p-1 text-right relative overflow-hidden">
        {/* Depth visualization bar */}
        {maxTotal && (
          <div
            style={{
              backgroundColor: depthColor,
              width: `${depthPercentage}%`,
            }}
            className="absolute top-0 right-0 h-full"
          />
        )}
        <span className="relative z-10">{total}</span>
      </TableCell>
    </TableRow>
  );
};
